import { VariantProps, cva } from 'class-variance-authority';
import { forwardRef, ReactNode } from 'react';

import { ColorOrCustom, cn, dynamicStyling, resolveForegroundColor } from 'styles';

import { IconButton } from '../Buttons';
import { XIcon } from '../Icons';

const tagVariants = cva(
  'inline-flex select-none items-center justify-center gap-2 text-center font-medium',
  {
    variants: {
      size: {
        tiny: 'px-1 py-0.5 text-xs leading-none',
        small: 'px-2 py-[2px] text-xs leading-none',
        medium: 'px-2 py-0.5 text-sm leading-tight',
        large: 'px-2 py-1 text-base leading-tight',
      },
    },

    defaultVariants: {
      size: 'small',
    },
  },
);

const sizeToIconSize = {
  tiny: 10,
  small: 12,
  medium: 14,
  large: 16,
};

export type TagProps = {
  children: ReactNode;
  className?: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  action?: () => any;
  actionIcon?: ReactNode;

  color: ColorOrCustom;

  round?: boolean;
  shrink?: boolean;

  type?: 'filled' | 'outlined';
} & VariantProps<typeof tagVariants>;

export const Tag = forwardRef<HTMLSpanElement, TagProps>(
  (
    {
      children,
      action,
      actionIcon,
      className,
      round = true,
      color,
      shrink = false,
      type = 'filled',
      size = 'small',
    },
    ref,
  ) => {
    const isFilled = type === 'filled';

    return (
      <span
        ref={ref}
        {...dynamicStyling({
          bg: isFilled ? color : undefined,

          color: isFilled ? resolveForegroundColor(color) : color,

          borderColor: isFilled ? 'transparent' : color,

          className: cn(
            'border',

            shrink ? 'shrink' : 'shrink-0',

            round ? 'rounded-3xl' : 'rounded-sm',

            tagVariants({ size, className }),
          ),
        })}
      >
        {children}

        {!!action && (
          <IconButton onClick={action} size="fit" className="text-current">
            {actionIcon || <XIcon size={sizeToIconSize[size!]} />}
          </IconButton>
        )}
      </span>
    );
  },
);

Tag.displayName = 'Tag';
